import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Scale, X, Award, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { useCompare } from "@/lib/compare";
import { FORMAT, FUEL_BG, TRANS_BG, BODY_BG, SITE } from "@/lib/site";

// Spec rows config: label + getter + optional 'best' direction
const SPECS = [
    { label: "Цена",          get: (c) => FORMAT.money(c.price),                 best: (c) => -c.price,   raw: (c) => c.price },
    { label: "Година",        get: (c) => String(c.year),                        best: (c) => c.year,     raw: (c) => c.year },
    { label: "Пробег",        get: (c) => FORMAT.km(c.mileage),                  best: (c) => -c.mileage, raw: (c) => c.mileage },
    { label: "Мощност",       get: (c) => FORMAT.hp(c.horsepower),               best: (c) => c.horsepower, raw: (c) => c.horsepower },
    { label: "Гориво",        get: (c) => FUEL_BG[c.fuel] || c.fuel },
    { label: "Скоростна кутия", get: (c) => TRANS_BG[c.transmission] || c.transmission },
    { label: "Каросерия",     get: (c) => BODY_BG[c.body_style] || c.body_style },
    { label: "Цвят",          get: (c) => c.color || "—" },
];

export default function ComparePage() {
    const [params] = useSearchParams();
    const { remove, clear } = useCompare();
    const ids = (params.get("ids") || "").split(",").filter(Boolean).slice(0, 3);

    const [cars, setCars] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (ids.length === 0) {
            setCars([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        Promise.all(
            ids.map((id) =>
                api.get(`/cars/${id}`).then((r) => r.data).catch(() => null),
            ),
        )
            .then((results) => setCars(results.filter(Boolean)))
            .finally(() => setLoading(false));
    }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

    // For each spec with `best`, determine winner index
    const winners = useMemo(() => {
        const w = {};
        if (cars.length < 2) return w;
        SPECS.forEach((s, i) => {
            if (!s.best) return;
            const values = cars.map((c) => s.best(c));
            const max = Math.max(...values);
            // Mark all cars whose value equals the max (handles ties)
            w[i] = values.map((v) => v === max);
        });
        return w;
    }, [cars]);

    if (loading) {
        return (
            <main className="pt-32 pb-24 min-h-screen bg-bg" data-testid="compare-page">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <p className="text-ink-muted">Зареждане…</p>
                </div>
            </main>
        );
    }

    return (
        <main className="relative pt-32 pb-32 min-h-screen bg-bg overflow-hidden" data-testid="compare-page">
            {/* KS logo watermark */}
            <div
                aria-hidden="true"
                className="pointer-events-none fixed inset-0 flex items-start justify-center opacity-[0.08] select-none -z-0"
                style={{
                    backgroundImage: "url(/ks-logo.png)",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center 30%",
                    backgroundSize: "min(80vw, 1100px) auto",
                }}
            />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                <Link
                    to="/cars"
                    className="inline-flex items-center gap-2 text-ink-muted hover:text-gold mb-8 text-sm"
                >
                    <ArrowLeft size={14} /> Към инвентара
                </Link>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="mb-12"
                >
                    <div className="flex items-center gap-3 mb-3 text-gold text-[11px] uppercase tracking-[0.32em]">
                        <Scale size={14} /> Сравнение
                    </div>
                    <h1 className="font-display text-5xl lg:text-6xl tracking-tightest">
                        Сравни автомобили
                    </h1>
                    <p className="mt-4 text-ink-muted">
                        {cars.length === 0
                            ? "Няма избрани автомобили за сравнение."
                            : `Сравняват се ${cars.length} автомобила. Полетата маркирани със златна точка показват най-доброто.`}
                    </p>
                </motion.div>

                {cars.length === 0 ? (
                    <div className="py-24 text-center">
                        <Link
                            to="/cars"
                            className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-bg px-6 py-3 rounded-full text-sm font-medium"
                        >
                            Изберете автомобили →
                        </Link>
                    </div>
                ) : (
                    <div className="overflow-x-auto -mx-6 lg:mx-0 px-6 lg:px-0">
                        <table
                            className="w-full border-collapse min-w-[640px]"
                            data-testid="compare-table"
                        >
                            <thead>
                                <tr>
                                    <th className="text-left text-[10px] uppercase tracking-[0.28em] text-ink-subtle pb-6 align-bottom w-40">
                                        Спецификация
                                    </th>
                                    {cars.map((c) => (
                                        <th
                                            key={c.id}
                                            className="text-left p-3 align-bottom"
                                            data-testid={`compare-col-${c.id}`}
                                        >
                                            <div className="relative bg-surface border border-line rounded-2xl p-4">
                                                <button
                                                    type="button"
                                                    onClick={() => remove(c.id)}
                                                    className="absolute top-2 right-2 w-7 h-7 rounded-full text-ink-muted hover:text-red-400 flex items-center justify-center"
                                                    aria-label="Премахни"
                                                    data-testid={`compare-col-remove-${c.id}`}
                                                >
                                                    <X size={14} />
                                                </button>
                                                <Link
                                                    to={`/cars/${c.id}`}
                                                    className="block aspect-[16/10] rounded-xl overflow-hidden bg-bg mb-4"
                                                >
                                                    {c.images?.[0] && (
                                                        <img
                                                            src={c.images[0]}
                                                            alt={`${c.brand} ${c.model}`}
                                                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                                        />
                                                    )}
                                                </Link>
                                                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">
                                                    {c.brand}
                                                </div>
                                                <div className="font-display text-xl mt-1 leading-tight">
                                                    {c.model}
                                                </div>
                                                <div className="font-display text-2xl text-gradient-gold mt-3">
                                                    {FORMAT.money(c.price)}
                                                </div>
                                            </div>
                                        </th>
                                    ))}
                                    {/* fill empty columns */}
                                    {Array.from({ length: 3 - cars.length }).map((_, i) => (
                                        <th key={`empty-${i}`} className="p-3 align-bottom">
                                            <Link
                                                to="/cars"
                                                className="block bg-surface/40 border border-dashed border-line rounded-2xl aspect-[16/22] hover:border-gold/40 text-center pt-20 text-ink-muted hover:text-gold text-sm"
                                                data-testid={`compare-add-slot-${i}`}
                                            >
                                                + Добави автомобил
                                            </Link>
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody>
                                {SPECS.map((spec, i) => (
                                    <tr
                                        key={spec.label}
                                        className="border-t border-line"
                                        data-testid={`compare-row-${spec.label}`}
                                    >
                                        <td className="py-5 pr-4 text-sm text-ink-muted uppercase tracking-[0.2em] text-[11px]">
                                            {spec.label}
                                        </td>
                                        {cars.map((c, j) => {
                                            const isWinner = winners[i]?.[j];
                                            return (
                                                <td
                                                    key={c.id}
                                                    className={`p-4 text-base ${
                                                        isWinner
                                                            ? "text-gold font-medium"
                                                            : "text-ink/90"
                                                    }`}
                                                >
                                                    <span className="inline-flex items-center gap-2">
                                                        {isWinner && (
                                                            <Award size={14} className="text-gold" />
                                                        )}
                                                        {spec.get(c)}
                                                    </span>
                                                </td>
                                            );
                                        })}
                                        {Array.from({ length: 3 - cars.length }).map((_, k) => (
                                            <td key={`gap-${k}`} className="p-4 text-ink-subtle">—</td>
                                        ))}
                                    </tr>
                                ))}

                                {/* Description row */}
                                <tr className="border-t border-line">
                                    <td className="py-5 pr-4 text-sm text-ink-muted uppercase tracking-[0.2em] text-[11px] align-top">
                                        Описание
                                    </td>
                                    {cars.map((c) => (
                                        <td key={c.id} className="p-4 text-sm text-ink-muted leading-relaxed align-top">
                                            {c.description || "—"}
                                        </td>
                                    ))}
                                    {Array.from({ length: 3 - cars.length }).map((_, k) => (
                                        <td key={`d-${k}`} className="p-4 text-ink-subtle">—</td>
                                    ))}
                                </tr>

                                {/* CTA row */}
                                <tr className="border-t border-line">
                                    <td className="py-5 pr-4 text-sm text-ink-muted uppercase tracking-[0.2em] text-[11px]">
                                        Контакт
                                    </td>
                                    {cars.map((c) => (
                                        <td key={c.id} className="p-4">
                                            <a
                                                href={`tel:${SITE.phoneRaw}`}
                                                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-bg px-4 py-2.5 rounded-full text-sm font-medium"
                                                data-testid={`compare-call-${c.id}`}
                                            >
                                                <Phone size={14} /> Обади се
                                            </a>
                                        </td>
                                    ))}
                                    {Array.from({ length: 3 - cars.length }).map((_, k) => (
                                        <td key={`c-${k}`} className="p-4 text-ink-subtle">—</td>
                                    ))}
                                </tr>
                            </tbody>
                        </table>

                        <div className="mt-10 flex justify-end">
                            <button
                                type="button"
                                onClick={() => {
                                    clear();
                                }}
                                className="text-sm text-ink-muted hover:text-red-400"
                                data-testid="compare-clear-all"
                            >
                                Изчисти сравнението
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </main>
    );
}
